import { useEffect, useState } from "react";
import { api, fmtGBP2, fmtDateUK } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Receipt } from "@phosphor-icons/react";

/** Right-side slide-out drawer with itemised ACCPAY invoice line breakdown. */
export default function SupplierDrawer({ open, onClose, token, classification, month, accent = "#0F172A" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/public/clients/${token}/invoices`, {
      params: { invoice_type: "ACCPAY", month, classification },
    })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [open, token, classification, month]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] p-0 overflow-y-auto" aria-describedby={undefined}>
        <SheetHeader className="p-6 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-3">
            <Receipt size={20} weight="duotone" style={{ color: accent }} />
            <SheetTitle className="font-display text-2xl tracking-tight">
              {classification === "WET" ? "Beverage" : "Food"} · Line-level invoices
            </SheetTitle>
          </div>
          <p className="text-xs text-[#475569] font-mono">{month}</p>
        </SheetHeader>
        {loading ? (
          <div className="p-8 text-sm text-[#475569]">Loading line items...</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-sm text-[#94A3B8]">No invoices for this period.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] px-6 h-10">Supplier</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] h-10">Description</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] h-10">Ref</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] h-10">Date</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-10">Amount</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] h-10 pr-6">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.flatMap((inv) =>
                (inv.line_items || [])
                  .filter((li) => li.classification === classification)
                  .map((li, i) => (
                    <TableRow key={`${inv.invoice_id}-${i}`} className="border-[#EEEEEE]">
                      <TableCell className="text-xs font-medium px-6 py-2.5">{inv.contact_name}</TableCell>
                      <TableCell className="text-xs py-2.5 text-[#475569]">{li.description}</TableCell>
                      <TableCell className="text-xs font-mono-num py-2.5">{inv.invoice_number}</TableCell>
                      <TableCell className="text-xs font-mono-num py-2.5">{fmtDateUK(inv.invoice_date)}</TableCell>
                      <TableCell className="text-xs font-mono-num text-right py-2.5" style={{ color: accent }}>
                        {fmtGBP2(li.line_amount)}
                      </TableCell>
                      <TableCell className="text-xs py-2.5 pr-6">
                        <span className={`px-1.5 py-0.5 rounded text-[11px] uppercase tracking-wider ${
                          inv.status === "PAID" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                        }`}>{inv.status}</span>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        )}
      </SheetContent>
    </Sheet>
  );
}
